<template lang="pug">
  .column
    krea-evaluation-method.m-bottom-2(v-if='showEvaluationMethod' edit)
    .width-100.custom-fields.column.m-bottom-2(v-if='customFields && customKreaFields.length')
      krea-custom-field(
        v-for='field of customKreaFields'
        :key='field.id'
        :field='field'
        edit
      )
    .column.m-bottom-2#globalValuationSection
      h2.title-medium.m-bottom-1 {{ innerI18n.t("valuation.valuationTitle") }}
      krea-text-editor(
        placeholderHeight='200px'
        height='400px'
        name="globalValuationSection"
        @input='editValuation'
        :value='activeLocalizedResource.globalValuationSection || ""'
        edit
        :info='valuationInfo'
        video image strike underline bold orderedList unorderedList removeMarks heading italic undo redo linkOption removeLink quote
        :placeholder='innerI18n.t("krea.resource.valuation.valuationPlaceholder")'
      )
    .column.m-bottom-2
      v-popover(
        placement='top-start'
        :delay='{show: 100, hide: 600}'
        trigger='hover click'
        popoverClass='krea-actions-popover'
        :container='false'
      )
        .width-100
          h2.title-medium {{ innerI18n.t("valuation.downloadableContent") }}
          .column.m-top-1
            .row.middle.material(v-for='mat in activeLocalizedResource.globalValuationMaterials' :key='mat.id || mat.temporalId')
              h2.text-normal.m-right-2 {{ mat.description }}
              a(target='_blank' :href='mat.fileURI' rel='norreferer noopener')
                simple-svg.dark-gray-icon(:filepath='downloadIcon' width='15px' height='15px')
              button(@click='removeValuationMaterial(mat.id || mat.temporalId)')
                simple-svg.m-left-1.dark-gray-icon(:filepath='trashIcon' width='16px' height='16px')
          label.primary-button.no-border.button-loading.m-top-2(v-if='uploading') {{ innerI18n.t('krea.resource.valuation.uploading') }}
          label.secondary-button.with-icon.m-top-2
            input.hidden(type='file' ref='fileInput' @change='uploadValuationMaterials')
            simple-svg.m-right-1(:filepath='uploadIcon' width='18px' height='18px')
            | {{ innerI18n.t("krea.resource.valuation.uploadFile") }}
          .row.middle.m-top-1(v-if='fileSizeError')
            simple-svg.red-icon.m-right-1.red-stroke-icon(:filepath='errorIcon' width='16px' height='16px')
            h2.text-normal.red {{ innerI18n.t("krea.resource.valuation.fileSizeError") }}
        template(slot='popover')
          actions(placement='start' :info='downloadableMaterialsInfo')
</template>

<script>
import KreaTextEditor from '@/components/Krea/Inputs/KreaTextEditor'
import uploadIcon from '@/assets/ic_upload.svg'
import { mapActions, mapState } from 'vuex'
import { languagesCodesToString } from '../../../constants/languages'
import { uploadFile } from '../../../services/FileUploadService'
import { getBase64, getExtension } from '../../../utils/file'
import { isError } from '../../../utils/api'
import trashIcon from '@/assets/krea/ic_trash.svg'
import downloadIcon from '@/assets/ic_download.svg'
import uniqid from 'uniqid'
import errorIcon from '@/assets/ic_stop.svg'
import Actions from '@/components/Krea/Inputs/Actions/Actions'
import { innerI18n } from '../../../main'
import kreaResourceRedirection from '@/mixins/kreaResourceRedirection'
import KreaEvaluationMethod from '../Inputs/KreaEvaluationMethod'
import { kreaCustomFieldTypes } from '@/constants/kreaCustom'
import KreaCustomField from '../Inputs/KreaCustomField'

export default {
  mixins: [kreaResourceRedirection],
  components: { KreaTextEditor, Actions, KreaEvaluationMethod, KreaCustomField },
  data() {
    return {
      errorIcon,
      innerI18n,
      downloadIcon,
      uploadIcon,
      trashIcon,
      fileSizeError: false,
      uploading: false
    }
  },
  computed: {
    ...mapState({
      activeLanguage: state => state.kreaResource.activeLanguage,
      resource: state => state.kreaResource.resource,
      innerLocale: state => state.behavior.innerLocale,
      customFields: state => state.userInfo.customFields
    }),
    customKreaFields() {
      return this.customFields && this.customFields.customizedKreaFields
        .filter(field => field.type === kreaCustomFieldTypes.resource && field.location === 'teacherValuationSection')
    },
    showEvaluationMethod() {
      return this.customFields.visibleEvaluationMethod
    },
    kreaContentGuide() {
      return process.env[`VUE_APP_KREA_CREATE_CONTENT_GUIDE_${this.innerLocale}`]
    },
    activeLocalizedResource() {
      return this.resource.localizedResources.find(res => languagesCodesToString[res.locale] === this.activeLanguage)
    },
    valuationInfo() {
      return {
        title: innerI18n.t('krea.resource.valuation.valuationInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.valuation.valuationInfo')]
      }
    },
    downloadableMaterialsInfo() {
      return {
        title: innerI18n.t('krea.resource.valuation.downloadableMaterialsInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [
          innerI18n.t('krea.resource.valuation.downloadableMaterialsInfo1'),
          innerI18n.t('krea.resource.valuation.downloadableMaterialsInfo2'),
          innerI18n.t('krea.resource.valuation.downloadableMaterialsInfo3')
        ]
      }
    }
  },
  methods: {
    ...mapActions([
      'editKreaLocalizedResourceField',
      'addKreaResourceValuationMaterial',
      'removeKreaResourceValuationMaterial'
    ]),
    editValuation(value, field) {
      this.editKreaLocalizedResourceField({ field, value, locale: this.activeLanguage })
    },
    removeValuationMaterial(id) {
      this.removeKreaResourceValuationMaterial({ materialId: id, locale: this.activeLanguage })
    },
    async uploadValuationMaterials(evt) {
      this.fileSizeError = false
      const file = evt.target.files[0]
      const fileExtension = getExtension(file.name)
      const fileSizeMb = (file.size / (1024 * 1024).toFixed(2))
      if (fileSizeMb > 10) {
        this.fileSizeError = true
      } else {
        this.uploading = true
        const content = await getBase64(file)
        const response = await uploadFile({ content, extension: fileExtension })
        const success = !isError(response.status)
        if (success) {
          this.fileUrl = response.data
          this.addKreaResourceValuationMaterial({
            newMaterial: {
              description: file.name,
              temporalId: uniqid(),
              fileURI: response.data
            },
            locale: this.activeLanguage
          })
        }
      }
      this.$refs.fileInput.value = null
      this.uploading = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.custom-fields{
  gap: 10px;
}

.secondary-button{
  cursor: pointer;
}

.hidden{
  display: none;
}

.material{
  border-radius: 5px;
  margin: 5px;
  padding: 5px;
}

@keyframes loading-bar {
  0% { background: linear-gradient(to right, $green 0%, $gray 0%); }
  25% { background: linear-gradient(to right, $green 50%, $gray 50%); }
  50% { background: linear-gradient(to right, $green 75%, $gray 75%); }
  100% { background: linear-gradient(to right, $green 90%, $gray 90%); }
}

.button-loading {
  background-color: $gray;
  background: linear-gradient(to right, $green 90%, $gray 90%);
  animation: loading-bar 5s;
  border: none;
}
</style>
